import { t } from 'i18next';
import { CallRow } from '@/services/call-log-service/call-log-service.types';
import SmsLogo from '@/assets/icons/sms-logo.svg?react';
import EmailLogo from '@/assets/icons/email.svg?react';
import ChatBotLogo from '@/assets/icons/chatbot.svg?react';
import Icon, { RobotOutlined } from '@ant-design/icons';
import Pause from '@/assets/icons/pause-circle.svg?react';
import Live from '@/assets/icons/transcript.svg?react';
import Play from '@/assets/icons/play.svg?react';
import styles from './call-logs-table.module.scss';
import IconButton from '../icon-button/icon-button';
import { calculateSecondsToMinutes, hasPlayableDuration, playableSeconds } from '@/utils/helpers';
import { Dispatch, SetStateAction } from 'react';
import { LoadingOutlined } from '@ant-design/icons';

export const generateDataSource = (data: CallRow[]) => {
  return data?.map((element) => ({
    key: element.id,
    ...element,
  }));
};

export const getCallIcon = (
  call: CallRow,
  selectedRecord?: CallRow,
  isPlaying: boolean = false
): React.ReactElement => {
  if (call.status === 'live') {
    return <Live />;
  }

  if (call.id === selectedRecord?.id && isPlaying) {
    return <Pause />;
  }

  return <Play />;
};

export const renderCallControl = (
  record: CallRow,
  duration: CallRow['duration'],
  selectedRecord?: CallRow,
  onSelectRecord?: (record: CallRow) => void,
  setIsPlaying?: Dispatch<SetStateAction<boolean>>,
  loadingRecordId?: number,
  isPlaying: boolean = false
) => {
  switch (record.type) {
    case 'TextConversation':
      return (
        <div className={styles.conversationIconWrapper}>
          <Icon className={styles.conversationIcon} component={SmsLogo} />
        </div>
      );

    case 'EmailConversation':
      return (
        <div className={styles.conversationIconWrapper}>
          <Icon className={styles.emailIcon} component={EmailLogo} />
        </div>
      );

    case 'ChatConversation':
      return (
        <div className={styles.conversationIconWrapper}>
          <Icon className={styles.chatbotIcon} component={ChatBotLogo} />
        </div>
      );

    case 'ElevenLabsCall':
      return (
        <div className={styles.conversationIconWrapper}>
          <RobotOutlined className={styles.robotIcon} />
        </div>
      );

    case 'AgentCall':
    case 'OutboundCall':
    case 'AnalyticsCall': {
      if (!hasPlayableDuration(duration) && record.status !== 'live') {
        return <p className={styles.noRecords}>---</p>;
      }

      if (record.status === 'live') {
        return <Icon className={styles.callIcon} component={() => getCallIcon(record)} />;
      }

      const playing = record.id === selectedRecord?.id && isPlaying;

      return (
        <IconButton
          shape="circle-outline"
          label={
            playing
              ? t('conversations:table.pauseRecording')
              : t('conversations:table.playRecording')
          }
          disabled={loadingRecordId === record.id}
          onClick={(event) => {
            // The row navigates to the drawer, so this click must not reach it.
            event.stopPropagation();
            onSelectRecord?.(record);
            if (record.id === selectedRecord?.id && setIsPlaying) {
              setIsPlaying((prev) => !prev);
            }
          }}
        >
          {loadingRecordId === record.id ? (
            <LoadingOutlined spin />
          ) : (
            getCallIcon(record, selectedRecord, isPlaying)
          )}
        </IconButton>
      );
    }

    default:
      return null;
  }
};

/** The mockup renders duration in its own muted column, as M:SS or LIVE. */
export const renderDuration = (record: CallRow) => {
  if (record.status === 'live') return t('conversations:table.liveDuration');
  const seconds = playableSeconds(record.duration);
  if (seconds === null) return '';
  return calculateSecondsToMinutes(seconds).toUpperCase();
};
